// usePanelLayout — the rail + dock widths (device-local display chrome,
// localStorage-persisted like the theme) + the drag-to-expand pull state and
// the dock's release handler. Split out of useShellFoundation (C1 #9). The
// rail's release handler stays in the foundation because it also reveals the
// rail (a navHost-dependent intent); everything width-shaped lives here.

import { type Dispatch, useState } from "react";
import type { FrameTransition } from "../frame.js";
import { type PanelDragConfig, panelDragSettle } from "../panelDragOpen.js";
import {
    clampDock,
    clampRail,
    DOCK,
    OPEN_PULL_THRESHOLD_PX,
    RAIL,
    RAIL_COLLAPSED_PX,
    readPanelLayout,
    writePanelLayout,
} from "../panelLayout.js";

export const usePanelLayout = (dispatch: Dispatch<FrameTransition>) => {
    const [panelLayout, setPanelLayout] = useState(() => readPanelLayout());
    const resizeRail = (growPx: number) =>
        setPanelLayout((l) => {
            const next = { ...l, railWidth: clampRail(l.railWidth + growPx) };
            writePanelLayout(next);
            return next;
        });
    const resizeDock = (growPx: number) =>
        setPanelLayout((l) => {
            const next = { ...l, dockWidth: clampDock(l.dockWidth + growPx) };
            writePanelLayout(next);
            return next;
        });
    const resetRail = () =>
        setPanelLayout((l) => {
            const next = { ...l, railWidth: RAIL.def };
            writePanelLayout(next);
            return next;
        });
    const resetDock = () =>
        setPanelLayout((l) => {
            const next = { ...l, dockWidth: DOCK.def };
            writePanelLayout(next);
            return next;
        });

    // Drag-to-expand (owner request): a collapsed panel can be PULLED open by
    // its inner edge. The in-flight pull distance (signed "positive = wider")
    // lives here as transient display chrome — null between gestures.
    const [dockDragPull, setDockDragPull] = useState<number | null>(null);
    const [railDragPull, setRailDragPull] = useState<number | null>(null);
    // The dock's collapsed body is zero-width; the rail's is the icon rail.
    const dockDragCfg: PanelDragConfig = {
        baseWidthPx: 0,
        minWidthPx: DOCK.min,
        maxWidthPx: DOCK.max,
        openThresholdPx: OPEN_PULL_THRESHOLD_PX,
    };
    const railDragCfg: PanelDragConfig = {
        baseWidthPx: RAIL_COLLAPSED_PX,
        minWidthPx: RAIL.min,
        maxWidthPx: RAIL.max,
        openThresholdPx: OPEN_PULL_THRESHOLD_PX,
    };
    // Release a dock drag-to-expand pull: past the threshold, persist the pulled
    // width and open the panel on the chat (the SAME `summon-slot` the open
    // handle's click fires); short of it, snap back (clear the pull, dispatch
    // nothing).
    const endDockDrag = (pullPx: number) => {
        setDockDragPull(null);
        const settle = panelDragSettle(pullPx, dockDragCfg);
        if (settle.type !== "open") return;
        setPanelLayout((l) => {
            const next = { ...l, dockWidth: clampDock(settle.widthPx) };
            writePanelLayout(next);
            return next;
        });
        dispatch({ type: "summon-slot", slot: "assistant" });
    };

    return {
        panelLayout,
        setPanelLayout,
        resizeRail,
        resizeDock,
        resetRail,
        resetDock,
        dockDragPull,
        setDockDragPull,
        railDragPull,
        setRailDragPull,
        dockDragCfg,
        railDragCfg,
        endDockDrag,
    };
};
